import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, ShieldCheck, AlertTriangle, Radio } from "lucide-react";

const NET = {
  TRC20: { label: "TRC20 · Tron", color: "red", note: "Only send USDT-TRC20 (Tron network) to this address." },
  BEP20: { label: "BEP20 · BSC", color: "amber", note: "Only send USDT-BEP20 (BNB Smart Chain) to this address." },
};

export default function Deposit() {
  const [addresses, setAddresses] = useState([]);
  const [active, setActive] = useState("TRC20");
  const [autoVerify, setAutoVerify] = useState(false);

  useEffect(() => {
    api.get("/deposit-addresses").then(({ data }) => {
      setAddresses(data.addresses);
      setAutoVerify(data.auto_verify_active);
    });
  }, []);

  const current = addresses.find((a) => a.network === active);
  const copy = () => { navigator.clipboard.writeText(current.address); toast.success("Address copied!"); };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-1">Deposit USDT</h1>
      <p className="text-sm text-slate-400 mb-6">Send USDT to your unique address — your balance is auto-credited the moment it's verified on-chain.</p>

      <div className={`mb-6 flex items-center gap-2 rounded-xl border p-3 text-sm ${autoVerify ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`}
        data-testid="auto-verify-status">
        <Radio className={`h-4 w-4 ${autoVerify ? "animate-pulse" : ""}`} />
        {autoVerify
          ? "Blockchain auto-verification is ACTIVE — monitoring the TRON/BSC grid."
          : "Auto-verification is currently OFF (admin needs to add blockchain API keys). Your deposit is safe — it will be auto-credited as soon as keys are added."}
      </div>

      <div className="flex gap-2 mb-6">
        {["TRC20", "BEP20"].map((n) => (
          <button key={n} data-testid={`deposit-${n.toLowerCase()}-tab`} onClick={() => setActive(n)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
              active === n ? "bg-emerald-500 text-white" : "bg-white/5 text-slate-400 hover:text-white"
            }`}>
            {NET[n].label}
          </button>
        ))}
      </div>

      {current && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-surface rounded-2xl p-6 flex flex-col items-center">
            <div className="bg-white p-4 rounded-2xl">
              <QRCodeSVG value={current.address} size={200} />
            </div>
            <Badge className="mt-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              <ShieldCheck className="h-3 w-3 mr-1" /> Deposit only
            </Badge>
          </div>
          <div className="card-surface rounded-2xl p-6">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">{NET[active].label} Address</div>
            <div className="font-mono text-sm break-all bg-white/5 rounded-xl p-4 mb-4" data-testid="deposit-address-value">
              {current.address}
            </div>
            <Button data-testid="copy-deposit-address-button" onClick={copy}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white mb-4">
              <Copy className="h-4 w-4 mr-2" /> Copy Address
            </Button>
            <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{NET[active].note} Funds sent on the wrong network may be permanently lost.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
